import React, { useState, useEffect, useRef, useContext } from 'react';
import axios from 'axios';
import { Button, Modal } from "react-bootstrap";
import Table from "../../components/Theme/Table.js";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Spinner from "../../components/Spinner";
import { RoleContext } from "../../components/RoleContext";
import { handleExportClick } from "../../components/exportFile";
import { Checkbox, Modal as AntdModal } from "antd";
import "../../components/modal.css";

import { 
  Edit as EditIcon,
  Navigation as ApproachIcon,
  Info as InfoIcon,
  LocationOn as LocationIcon,
  Percent as PercentIcon,
  AttachMoney as MoneyIcon,
  LocalHospital as EmergencyIcon
} from '@mui/icons-material';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';

const ApproachDistanceConfig = () => {
  const printableRef = useRef(null);
  
  const [configs, setConfigs] = useState([]);
  const [id, setId] = useState(null);
  const [loading, setLoading] = useState(true);

  const roleData = useContext(RoleContext);
  const role = JSON.parse(roleData);

  // Modal states
  const [displayEditModal, setDisplayEditModal] = useState(false);
  const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState(null);
  
  // Export modal states
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);

  // Form data for add/edit
  const [formData, setFormData] = useState({
    enableApproachFee: true,
    minApproachDistance: 2.0,
    approachRatePercentage: 50.0,
    maxApproachFee: 200.0,
    applyToEmergencies: false,
    showBreakdownInEstimate: true,
    isActive: true
  });

  // Validation errors
  const [errors, setErrors] = useState({});

  const getConfigs = async () => {
    try {
      const token = localStorage.getItem("d4uToken");
      
      const response = await axios.get('/approach-distance-configs', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      console.log('API Response:', response.data);
      
      const configData = response.data.data || response.data || [];
      setConfigs(Array.isArray(configData) ? configData : []);
      setLoading(false);
    } catch (error) {
      console.error('API Error:', error);
      notifyError('Failed to fetch approach distance configurations');
      setLoading(false);
    }
  };

  // Modal handlers
  const showEditModal = (arg) => {
    const config = typeof arg === 'object' && arg !== null
      ? arg
      : configs.find(c => String(c.id) === String(arg));

    if (config) {
      setId(config.id);
      setFormData({
        enableApproachFee: config.enableApproachFee,
        minApproachDistance: config.minApproachDistance,
        approachRatePercentage: config.approachRatePercentage,
        maxApproachFee: config.maxApproachFee,
        applyToEmergencies: config.applyToEmergencies,
        showBreakdownInEstimate: config.showBreakdownInEstimate,
        isActive: config.isActive
      });
      setErrors({});
      setDisplayEditModal(true);
    }
  };

  const hideEditModal = () => {
    setDisplayEditModal(false);
  };

  const showDeleteModal = (id, name) => {
    setId(id);
    setDeleteMessage(`Are you sure you want to delete configuration "${name}"?`);
    setDisplayConfirmationModal(true);
  };

  const hideConfirmationModal = () => {
    setDisplayConfirmationModal(false);
  };

  const submitDelete = async (id) => {
    try {
      const token = localStorage.getItem("d4uToken");
      
      await axios.delete(`/approach-distance-configs/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      getConfigs();
      setDisplayConfirmationModal(false);
      notify('Configuration deleted successfully');
    } catch (error) {
      console.error('Delete Error:', error);
      notifyError('Failed to delete configuration');
    }
  };

  const activateConfig = async (id) => {
    try {
      const token = localStorage.getItem("d4uToken");
      
      await axios.put(`/approach-distance-configs/${id}/activate`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      getConfigs();
      notify('Configuration activated successfully');
    } catch (error) {
      console.error('Activate Error:', error);
      notifyError('Failed to activate configuration');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.minApproachDistance && formData.minApproachDistance !== 0) {
      newErrors.minApproachDistance = 'Minimum approach distance is required';
    } else if (parseFloat(formData.minApproachDistance) < 0) {
      newErrors.minApproachDistance = 'Minimum distance must be positive or zero';
    }
    
    if (!formData.approachRatePercentage && formData.approachRatePercentage !== 0) {
      newErrors.approachRatePercentage = 'Approach rate percentage is required';
    } else if (parseFloat(formData.approachRatePercentage) < 0) {
      newErrors.approachRatePercentage = 'Percentage must be 0 or greater';
    }
    
    if (!formData.maxApproachFee || formData.maxApproachFee === '') {
      newErrors.maxApproachFee = 'Maximum approach fee is required';
    } else if (parseFloat(formData.maxApproachFee) <= 0) {
      newErrors.maxApproachFee = 'Maximum fee must be greater than 0';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const token = localStorage.getItem("d4uToken");
      
      console.log('Submitting form data:', formData);
      
      // Edit config
      await axios.put(`/approach-distance-configs/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      notify('Configuration updated successfully');
      hideEditModal();
      getConfigs();
    } catch (error) {
      console.error('Submit Error:', error);
      const errorMessage = error.response?.data?.message || 'Operation failed';
      notifyError(errorMessage);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  // Toast notifications
  const notify = (message = "Operation completed successfully.") =>
    toast.success(message, {
      position: "top-right",
      autoClose: 1000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });

  const notifyError = (message = "Something went wrong ...") =>
    toast.error(message, {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });

  useEffect(() => {
    getConfigs();
  }, []);

  // Table columns
  const columns = React.useMemo(
    () => [
      {
        Header: "Configuration Settings",
        columns: [
          {
            Header: "ID",
            accessor: "id",
          },
          {
            Header: "Min Distance (km)",
            accessor: "minApproachDistance",
            Cell: (row) => `${row.value} km`,
          },
          {
            Header: "Rate (%)",
            accessor: "approachRatePercentage",
            Cell: (row) => `${row.value}%`,
          },
          {
            Header: "Max Fee (Birr)",
            accessor: "maxApproachFee",
            Cell: (row) => `${row.value} Birr`,
          },
        ],
      },
      {
        Header: "Settings",
        columns: [
          {
            Header: "Feature Enabled",
            accessor: "enableApproachFee",
            disableFilters: true,
            Cell: (row) => (
              <Chip 
                label={row.value ? 'Yes' : 'No'} 
                color={row.value ? 'success' : 'default'}
                variant="filled"
                size="small"
              />
            ),
          },
          {
            Header: "Apply to Emergency",
            accessor: "applyToEmergencies",
            disableFilters: true,
            Cell: (row) => (
              <Chip 
                label={row.value ? 'Yes' : 'No'} 
                color={row.value ? 'warning' : 'default'}
                variant="filled"
                size="small"
              />
            ),
          },
          {
            Header: "Status",
            accessor: "isActive",
            disableFilters: true,
            Cell: (row) => (
              <Chip 
                label={row.value ? 'Active' : 'Inactive'} 
                color={row.value ? 'success' : 'default'}
                variant="filled"
                sx={{
                  fontWeight: 'bold',
                  borderRadius: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              />
            ),
          },
          {
            Header: "Action",
            accessor: "action",
            disableFilters: true,
            Cell: (row) => (
              <div style={{ 
                display: "flex", 
                gap: "15px", 
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Tooltip title="Edit Configuration">
                  <span>
                    <EditIcon 
                      color="primary" 
                      sx={{ 
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'scale(1.2)',
                        }
                      }}
                      onClick={() => showEditModal(row.row.original)}
                    />
                  </span>
                </Tooltip>
              </div>
            ),
          },
        ],
      },
    ],
    []
  );

  // Export functionality
  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = () => {
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const onChange = (checkedValues) => {
    setSelectedColumns(checkedValues);
  };

  const exportColumns = [
    { label: "ID", value: "id" },
    { label: "Min Distance", value: "minApproachDistance" },
    { label: "Rate Percentage", value: "approachRatePercentage" },
    { label: "Max Fee", value: "maxApproachFee" },
    { label: "Enabled", value: "enableApproachFee" },
    { label: "Apply to Emergency", value: "applyToEmergencies" },
    { label: "Active", value: "isActive" },
  ];

  if (loading) {
    return <Spinner />;
  }

  // Get active configuration for display
  const activeConfig = configs.find(c => c.isActive) || {
    minApproachDistance: 2.0,
    approachRatePercentage: 50.0,
    maxApproachFee: 200.0,
    applyToEmergencies: false
  };

  return (
    <>
      <ToastContainer />
      
      <div style={{ padding: '20px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px' 
        }}>
          <h2 style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            fontWeight: 'bold',
            margin: 0 
          }}>
            <ApproachIcon fontSize="large" />
            Approach Distance Configuration
          </h2>
          
          <div style={{ display: 'flex', gap: '10px' }}>
            {configs.length > 0 && (
              <Button 
                variant="primary"
                onClick={() => showEditModal(configs[0].id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <EditIcon /> Edit Configuration
              </Button>
            )}
            
            <Button 
              variant="success"
              onClick={showModal}
            >
              Export
            </Button>
          </div>
        </div>

        <div ref={printableRef}>
          <Table columns={columns} data={configs} />
        </div>

        {/* Info Section with Beautiful Cards */}
        <div style={{ marginTop: '30px' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px',
            marginBottom: '20px',
            padding: '15px',
            backgroundColor: '#e3f2fd',
            borderRadius: '10px',
            border: '2px solid #2196f3'
          }}>
            <InfoIcon style={{ fontSize: '32px', color: '#1976d2' }} />
            <div>
              <h4 style={{ margin: 0, color: '#1565c0', fontWeight: 'bold' }}>About Approach Distance Fee</h4>
              <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#424242' }}>
                The approach distance fee compensates drivers for traveling from their current location to the pickup point (dead-head miles). 
                For example: if the ambulance is at Summit and the patient is at Urael, the driver travels extra distance before starting the actual trip. 
                This fee ensures fair compensation for that "approach" distance.
              </p>
            </div>
          </div>

          {/* Feature Cards Grid */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px'
          }}>
            {/* Min Distance Card */}
            <div style={{
              padding: '20px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: '1px solid #e0e0e0',
              transition: 'transform 0.2s, box-shadow 0.2s',
              cursor: 'default'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: '#e8f5e9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <LocationIcon style={{ fontSize: '28px', color: '#4caf50' }} />
                </div>
                <h5 style={{ margin: 0, fontWeight: 'bold', color: '#2e7d32' }}>Minimum Distance</h5>
              </div>
              <p style={{ margin: 0, fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                No fee is charged if the ambulance is within this distance from the pickup point. Current: <strong>{activeConfig.minApproachDistance} km</strong>
              </p>
            </div>

            {/* Rate Percentage Card */}
            <div style={{
              padding: '20px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: '1px solid #e0e0e0',
              transition: 'transform 0.2s, box-shadow 0.2s',
              cursor: 'default'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: '#fff3e0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <PercentIcon style={{ fontSize: '28px', color: '#ff9800' }} />
                </div>
                <h5 style={{ margin: 0, fontWeight: 'bold', color: '#e65100' }}>Rate Percentage</h5>
              </div>
              <p style={{ margin: 0, fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                Percentage of the vehicle's normal rate charged for approach distance. Current: <strong>{activeConfig.approachRatePercentage}%</strong>
              </p>
            </div>

            {/* Max Fee Card */}
            <div style={{
              padding: '20px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: '1px solid #e0e0e0',
              transition: 'transform 0.2s, box-shadow 0.2s',
              cursor: 'default'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: '#fce4ec',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <MoneyIcon style={{ fontSize: '28px', color: '#e91e63' }} />
                </div>
                <h5 style={{ margin: 0, fontWeight: 'bold', color: '#c2185b' }}>Maximum Fee Cap</h5>
              </div>
              <p style={{ margin: 0, fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                Cap to protect patients from excessive charges on very long approach distances. Current: <strong>{activeConfig.maxApproachFee} Birr</strong>
              </p>
            </div>

            {/* Emergency Application Card */}
            <div style={{
              padding: '20px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: '1px solid #e0e0e0',
              transition: 'transform 0.2s, box-shadow 0.2s',
              cursor: 'default'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: '#ffebee',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <EmergencyIcon style={{ fontSize: '28px', color: '#f44336' }} />
                </div>
                <h5 style={{ margin: 0, fontWeight: 'bold', color: '#d32f2f' }}>Emergency Trips</h5>
              </div>
              <p style={{ margin: 0, fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                Configure whether to charge approach fee for emergency trips. Current: <strong>{activeConfig.applyToEmergencies ? 'Applied' : 'Exempt'}</strong> ({activeConfig.applyToEmergencies ? 'fee charged' : 'no fee for emergencies'})
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <Modal
        style={{marginTop: "60px"}} 
        show={displayEditModal}
        onHide={hideEditModal}
        backdrop={false}
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Approach Distance Configuration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-12">
              <div className="card card-primary">
                <div className="card-body">
                  <div className="form-group">
                    <div className="custom-control custom-checkbox">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="enableApproachFee"
                        name="enableApproachFee"
                        checked={formData.enableApproachFee}
                        onChange={handleInputChange}
                      />
                      <label className="custom-control-label" htmlFor="enableApproachFee">
                        Enable Approach Fee
                      </label>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="minApproachDistance">Minimum Approach Distance (km)</label>
                    <input
                      type="number"
                      name="minApproachDistance"
                      value={formData.minApproachDistance}
                      onChange={handleInputChange}
                      className={errors.minApproachDistance ? "form-control input-error" : "form-control"}
                      step="0.1"
                      min="0"
                    />
                    {errors.minApproachDistance && <p className="error">{errors.minApproachDistance}</p>}
                    <small className="form-text text-muted">
                      No fee if ambulance is within this distance from pickup point
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="approachRatePercentage">Approach Rate Percentage (%)</label>
                    <input
                      type="number"
                      name="approachRatePercentage"
                      value={formData.approachRatePercentage}
                      onChange={handleInputChange}
                      className={errors.approachRatePercentage ? "form-control input-error" : "form-control"}
                      step="1"
                      min="0"
                    />
                    {errors.approachRatePercentage && <p className="error">{errors.approachRatePercentage}</p>}
                    <small className="form-text text-muted">
                      Percentage of vehicle's normal rate (e.g., 50% means half the regular rate)
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="maxApproachFee">Maximum Approach Fee (Birr)</label>
                    <input
                      type="number"
                      name="maxApproachFee"
                      value={formData.maxApproachFee}
                      onChange={handleInputChange}
                      className={errors.maxApproachFee ? "form-control input-error" : "form-control"}
                      step="10"
                      min="0"
                    />
                    {errors.maxApproachFee && <p className="error">{errors.maxApproachFee}</p>}
                    <small className="form-text text-muted">
                      Cap to protect patients from excessive charges
                    </small>
                  </div>

                  <div className="form-group">
                    <div className="custom-control custom-checkbox">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="applyToEmergencies"
                        name="applyToEmergencies"
                        checked={formData.applyToEmergencies}
                        onChange={handleInputChange}
                      />
                      <label className="custom-control-label" htmlFor="applyToEmergencies">
                        Charge Approach Fee for Emergency Trips
                      </label>
                      <small className="form-text text-muted">
                        <strong>✅ Checked:</strong> Emergency trips WILL be charged approach fee<br/>
                        <strong>❌ Unchecked (Recommended):</strong> Emergency trips are EXEMPT (no approach fee)
                      </small>
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="custom-control custom-checkbox">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="showBreakdownInEstimate"
                        name="showBreakdownInEstimate"
                        checked={formData.showBreakdownInEstimate}
                        onChange={handleInputChange}
                      />
                      <label className="custom-control-label" htmlFor="showBreakdownInEstimate">
                        Show Breakdown in Price Estimate
                      </label>
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="custom-control custom-checkbox">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="isActive"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                      />
                      <label className="custom-control-label" htmlFor="isActive">
                        Set as Active Configuration
                      </label>
                      <small className="form-text text-muted">
                        Only one configuration can be active at a time
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="default" onClick={hideEditModal}>
            Cancel
          </Button>
          <button onClick={handleSubmit} className="btn btn-success" type="button">
            Update
          </button>
        </Modal.Footer>
      </Modal>

      {/* Export Modal */}
      <AntdModal
        title="Select Columns to Export"
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="export"
            variant="primary"
            onClick={() => {
              handleExportClick(
                configs,
                selectedColumns,
                "ApproachDistanceConfig",
                printableRef
              );
              handleOk();
            }}
          >
            Export
          </Button>,
        ]}
      >
        <Checkbox.Group
          options={exportColumns}
          onChange={onChange}
          style={{ display: "flex", flexDirection: "column" }}
        />
      </AntdModal>
    </>
  );
};

export default ApproachDistanceConfig;
